import { useEffect, useState } from "react";
import { Routes, Route, Link, NavLink, useLocation } from "react-router-dom";
import { useAuth } from "@/stores/authStore";
import ProduktePage from "./pages/ProduktePage";
import KundenPage from "./pages/KundenPage";
import KundeDetailPage from "./pages/KundeDetailPage";
import BestellungenPage from "./pages/BestellungenPage";
import BestellungDetailPage from "./pages/BestellungDetailPage";
import AbosPage from "./pages/AbosPage";
import TagesplanPage from "./pages/TagesplanPage";
import BackofficeKassePage from "./pages/KassePage";
import MitarbeiterPage from "./pages/MitarbeiterPage";
import RechnungenPage from "./pages/RechnungenPage";
import SepaPage from "./pages/SepaPage";

export default function BackofficeModule() {
  const signOut = useAuth((s) => s.signOut);
  const [navOpen, setNavOpen] = useState(false);
  const location = useLocation();

  // Drawer schließt sich bei jedem Routenwechsel.
  useEffect(() => setNavOpen(false), [location.pathname]);

  // body-scroll lock solange Drawer offen
  useEffect(() => {
    document.body.style.overflow = navOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [navOpen]);

  return (
    <div className="min-h-screen bg-hof-50">
      {/* Mobile Top-Bar */}
      <header className="md:hidden sticky top-0 z-30 bg-white border-b border-hof-100 flex items-center justify-between px-4 py-3">
        <button
          onClick={() => setNavOpen(true)}
          aria-label="Menü öffnen"
          className="btn-ghost min-w-touch min-h-touch p-2"
        >
          <BurgerIcon />
        </button>
        <Link to="/backoffice" className="flex items-center gap-2">
          <img src="/logo-emblem.png" alt="" className="h-8" />
          <span className="font-brand font-bold text-lg text-wappen-700">Hof Sirges</span>
        </Link>
        <div className="w-10" /> {/* Spacer für visuelle Mitte */}
      </header>

      <div className="flex">
        {/* Backdrop für Mobile-Drawer */}
        {navOpen && (
          <button
            aria-label="Menü schließen"
            onClick={() => setNavOpen(false)}
            className="md:hidden fixed inset-0 z-40 bg-hof-900/40"
          />
        )}

        {/* Sidebar — Desktop sticky, Mobile als Drawer */}
        <aside
          className={`
            bg-white border-r border-hof-100 z-50
            md:sticky md:top-0 md:h-screen md:w-60 md:translate-x-0 md:block
            fixed top-0 left-0 bottom-0 w-72 max-w-[85vw]
            transition-transform duration-200 ease-out
            ${navOpen ? "translate-x-0" : "-translate-x-full"}
          `}
        >
          <div className="p-4 border-b border-hof-100 flex items-center justify-between">
            <div>
              <Link to="/backoffice" className="flex items-center gap-2">
                <img src="/logo-emblem.png" alt="" className="h-9" />
                <span className="font-brand font-bold text-xl text-wappen-700">Hof Sirges</span>
              </Link>
              <div className="text-xs text-hof-500 mt-1 ml-1">Backoffice</div>
            </div>
            <button
              onClick={() => setNavOpen(false)}
              aria-label="Menü schließen"
              className="md:hidden btn-ghost min-w-touch min-h-touch p-2"
            >
              <CloseIcon />
            </button>
          </div>

          <nav className="p-2 space-y-1">
            <BoNav to="/backoffice">Dashboard</BoNav>
            <BoNav to="/backoffice/bestellungen">Bestellungen</BoNav>
            <BoNav to="/backoffice/tagesplan">Tagesplan</BoNav>
            <BoNav to="/backoffice/abos">Abos</BoNav>
            <BoNav to="/backoffice/kunden">Kunden</BoNav>
            <BoNav to="/backoffice/produkte">Produkte</BoNav>
            <BoNav to="/backoffice/rechnungen">Rechnungen</BoNav>
            <BoNav to="/backoffice/sepa">SEPA</BoNav>
            <BoNav to="/backoffice/kasse">Kasse</BoNav>
            <BoNav to="/backoffice/mitarbeiter">Mitarbeiter</BoNav>
            <BoNav to="/backoffice/berichte">Berichte</BoNav>
            <BoNav to="/backoffice/einstellungen">Einstellungen</BoNav>
          </nav>
          <div className="p-2 border-t border-hof-100">
            <button onClick={signOut} className="btn-ghost w-full justify-start">Abmelden</button>
          </div>
        </aside>

        <main className="flex-1 px-4 py-4 md:px-6 md:py-6 max-w-7xl w-full">
          <Routes>
            <Route index element={<DashboardStub />} />
            <Route path="bestellungen" element={<BestellungenPage />} />
            <Route path="bestellungen/:id" element={<BestellungDetailPage />} />
            <Route path="abos" element={<AbosPage />} />
            <Route path="kasse" element={<BackofficeKassePage />} />
            <Route path="mitarbeiter" element={<MitarbeiterPage />} />
            <Route path="tagesplan" element={<TagesplanPage />} />
            <Route path="kunden" element={<KundenPage />} />
            <Route path="kunden/:id" element={<KundeDetailPage />} />
            <Route path="produkte" element={<ProduktePage />} />
            <Route path="rechnungen" element={<RechnungenPage />} />
            <Route path="sepa" element={<SepaPage />} />
              <Route path="berichte" element={<Stub title="Berichte" />} />
            <Route path="einstellungen" element={<Stub title="Einstellungen" />} />
          </Routes>
        </main>
      </div>
    </div>
  );
}

function BoNav({ to, children }: { to: string; children: React.ReactNode }) {
  return (
    <NavLink
      to={to}
      end
      className={({ isActive }) =>
        `block px-3 py-3 rounded-xl min-h-touch ${
          isActive ? "bg-wiese-100 text-wiese-800 font-medium" : "text-hof-700 hover:bg-hof-100"
        }`
      }
    >
      {children}
    </NavLink>
  );
}

function BurgerIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <line x1="4" y1="7"  x2="20" y2="7"  />
      <line x1="4" y1="12" x2="20" y2="12" />
      <line x1="4" y1="17" x2="20" y2="17" />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <line x1="6" y1="6" x2="18" y2="18" />
      <line x1="18" y1="6" x2="6" y2="18" />
    </svg>
  );
}

function DashboardStub() {
  return (
    <div className="space-y-6">
      <h1 className="text-3xl font-bold text-hof-900">Dashboard</h1>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Kpi label="Bestellungen heute" value="—" />
        <Kpi label="Aktive Touren" value="—" />
        <Kpi label="Offener Umsatz" value="— €" />
      </div>
      <div className="pill-info">Modul-Stub — KPI-Quellen werden in Modul 6 verkabelt.</div>
    </div>
  );
}

function Kpi({ label, value }: { label: string; value: string }) {
  return (
    <div className="card">
      <div className="text-sm text-hof-500">{label}</div>
      <div className="text-3xl font-bold text-hof-900 mt-1">{value}</div>
    </div>
  );
}

function Stub({ title }: { title: string }) {
  return (
    <div className="card">
      <h1 className="card-header">{title}</h1>
      <p className="text-hof-600">Modul-Stub.</p>
    </div>
  );
}
